import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, CurrencyEur, Eye, Trash, Plus, DotsThreeVertical, Archive, XCircle, CheckCircle, ArrowsLeftRight, ChatCircleText, FilePdf } from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "@/lib/fmt";
import AdminCreateRequestModal from "@/components/AdminCreateRequestModal";
import ClientAccountShareModal from "@/components/ClientAccountShareModal";
import ReferenceBadge from "@/components/ReferenceBadge";
import { downloadPdf } from "@/lib/pdf";

const statusColor = (s) => ({
  pending_price: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  priced: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  accepted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  converted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
  archived: "bg-zinc-700/30 text-zinc-400 border-zinc-600",
}[s] || "bg-zinc-700/30 text-zinc-300 border-zinc-600");

const STATUS_LABEL = {
  pending_price: "En attente de tarif",
  priced: "Tarif envoyé",
  accepted: "Acceptée",
  rejected: "Refusée",
  cancelled: "Annulée",
  converted: "Convertie en course",
  archived: "Archivée",
};

// Robust agency-vs-private classifier. Backend now sets `client_type` on every
// new request and a startup backfill labels legacy ones. We also keep a
// last-resort fallback for any edge case where the field is missing.
const isPrivateClient = (r) =>
  r.client_type === "private" || (r.client_type == null && (r.is_guest || r.linked_at));

// Small visual marker placed next to the agency/client name in the admin list.
function ClientTypeBadge({ request, className = "" }) {
  const isPrivate = isPrivateClient(request);
  if (isPrivate) {
    return (
      <span
        data-testid={`client-type-private-${request.id}`}
        title="Client privé (auto-créé via demande publique ou créé par admin)"
        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40 ${className}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
        Client privé
      </span>
    );
  }
  return (
    <span
      data-testid={`client-type-agency-${request.id}`}
      title="Demande émise par une agence partenaire"
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-sky-500/15 text-sky-300 border-sky-500/40 ${className}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
      Agence
    </span>
  );
}

export default function RideRequestsAdmin() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [priceModal, setPriceModal] = useState(null);
  const [priceVal, setPriceVal] = useState("");
  const [pricingMode, setPricingMode] = useState("one_way_each"); // round-trip only
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("open");
  const [createOpen, setCreateOpen] = useState(false);
  const [shareModal, setShareModal] = useState(null); // { data, ride }

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/ride-requests");
      setRequests(data);
    } catch {}
  }, []);
  useEffect(() => {
    fetchRequests();
    const iv = setInterval(fetchRequests, 10000);
    return () => clearInterval(iv);
  }, [fetchRequests]);

  const openPrice = (r) => {
    setPriceVal(r.admin_price ? String(r.admin_price) : "");
    setPricingMode(r.pricing_mode || "one_way_each");
    setPriceModal(r);
  };

  const submitPrice = async () => {
    if (!priceModal) return;
    const p = parseFloat(priceVal);
    if (!p || p <= 0) {
      toast.error("Entrez un tarif valide");
      return;
    }
    const payload = { price: p };
    if (priceModal.is_round_trip) {
      payload.pricing_mode = pricingMode;
    }
    try {
      await api.put(`/ride-requests/${priceModal.id}/price`, payload);
      toast.success("Tarif envoyé à l'agence");
      setPriceModal(null);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Supprimer la demande de ${r.client_name || r.agency_name || "ce client"} ?`)) return;
    try {
      await api.delete(`/ride-requests/${r.id}`);
      toast.success("Supprimée");
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const convertGuest = async (r) => {
    if (!r.admin_price || r.admin_price <= 0) { toast.error("Définissez d'abord un tarif"); return; }
    if (r.is_round_trip && !r.pricing_mode) {
      toast.error("Veuillez d'abord choisir le mode de tarification (via « Définir le tarif »)");
      return;
    }
    const legs = r.is_round_trip ? 2 : 1;
    const mode = r.pricing_mode;
    const per = mode === "round_trip_total" ? (Number(r.admin_price) / 2).toFixed(2) : Number(r.admin_price).toFixed(2);
    const total = mode === "round_trip_total" ? Number(r.admin_price).toFixed(2) : (Number(r.admin_price) * legs).toFixed(2);
    // Confirmation message — slightly different wording when admin is forcing a
    // conversion (override) on a request that would normally wait for the
    // agency/client to validate, so the action is unambiguous.
    const isOverride = !r.is_guest;
    const headline = r.is_round_trip
      ? `Convertir la demande aller-retour de ${r.client_name} ?\n\n${legs} courses seront créées :\n• Aller  : ${per} EUR\n• Retour : ${per} EUR\n• Total chauffeur : ${total} EUR`
      : `Convertir la demande de ${r.client_name} en course (1 course · ${per} EUR) ?`;
    const msg = isOverride
      ? `${headline}\n\n⚠️ La validation client sera contournée — utilisez ce bouton uniquement si le client a déjà confirmé (téléphone, WhatsApp, SMS).`
      : headline;
    if (!window.confirm(msg)) return;
    try {
      const { data: resp } = await api.post(`/ride-requests/${r.id}/admin-convert`);
      // Account status feedback + communication modal (admin-controlled — no auto email)
      const acc = resp?.client_account;
      if (acc?.status === "created") toast.success("Compte client créé");
      else if (acc?.status === "existing") toast.success("Client existant lié");
      else toast.success(r.is_round_trip ? "Aller-retour converti (2 courses créées)" : "Demande convertie en course");
      // Show the share modal with ride details + pre-filled message
      setShareModal({
        data: resp,
        ride: {
          ...r,
          id: resp?.ride_id || r.id,
          is_round_trip: r.is_round_trip,
          return_date: r.return_date,
          return_time: r.return_time,
        },
      });
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const rejectReq = async (r) => {
    if (!window.confirm(`Refuser la demande de ${r.client_name || r.agency_name || "ce client"} ?`)) return;
    try { await api.post(`/ride-requests/${r.id}/reject`); toast.success("Demande refusée"); fetchRequests(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const archiveReq = async (r) => {
    if (!window.confirm(`Archiver la demande de ${r.client_name || r.agency_name || "ce client"} ?`)) return;
    try { await api.post(`/ride-requests/${r.id}/archive`); toast.success("Demande archivée"); fetchRequests(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const reopenShare = async (r) => {
    try {
      const { data } = await api.get(`/ride-requests/${r.id}/client-share`);
      setShareModal({ data, ride: { ...r, ...data.ride } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de récupérer les infos client");
    }
  };

  const view = (r) => {
    if (filter === "open") return !["converted", "rejected", "archived", "cancelled"].includes(r.status);
    if (filter === "converted") return r.status === "converted";
    if (filter === "rejected") return r.status === "rejected";
    if (filter === "archived") return r.status === "archived";
    return true;
  };
  const filtered = requests.filter(view);

  const ActionsMenu = ({ r, testidPrefix }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid={`${testidPrefix}-actions-${r.id}`}
          className="h-8 w-8 p-0 text-zinc-300 hover:text-white hover:bg-zinc-800 border border-zinc-800"
          aria-label="Plus d'actions"
        >
          <DotsThreeVertical size={18} weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#121212] border-zinc-800 text-zinc-100 min-w-[200px]">
        {r.admin_price != null && ["priced", "accepted", "converted"].includes(r.status) && (
          <DropdownMenuItem
            data-testid={`${testidPrefix}-quote-pdf-${r.id}`}
            onClick={() => downloadPdf(`/ride-requests/${r.id}/quote-pdf`)}
            className="cursor-pointer focus:bg-amber-500/10 focus:text-amber-300"
          >
            <FilePdf size={16} className="mr-2 text-amber-400" /> Télécharger le devis (PDF)
          </DropdownMenuItem>
        )}
        {r.status === "converted" && r.ride_id && (
          <DropdownMenuItem
            data-testid={`${testidPrefix}-voucher-pdf-${r.id}`}
            onClick={() => downloadPdf(`/rides/${r.ride_id}/voucher-pdf`)}
            className="cursor-pointer focus:bg-emerald-500/10 focus:text-emerald-300"
          >
            <FilePdf size={16} className="mr-2 text-emerald-400" /> Télécharger la confirmation
          </DropdownMenuItem>
        )}
        {r.status === "priced" && (
          <DropdownMenuItem
            data-testid={`${testidPrefix}-convert-${r.id}`}
            onClick={() => convertGuest(r)}
            className="cursor-pointer focus:bg-emerald-500/10 focus:text-emerald-300"
          >
            <CheckCircle size={16} className="mr-2 text-emerald-400" /> {r.is_guest ? "Convertir en course" : "Convertir en course (sans validation client)"}
          </DropdownMenuItem>
        )}
        {r.status === "converted" && r.client_email && (
          <DropdownMenuItem
            data-testid={`${testidPrefix}-reopen-share-${r.id}`}
            onClick={() => reopenShare(r)}
            className="cursor-pointer focus:bg-sky-500/10 focus:text-sky-300"
          >
            <ChatCircleText size={16} className="mr-2 text-sky-400" /> Ré-ouvrir le message
          </DropdownMenuItem>
        )}
        {!["rejected", "archived", "converted", "cancelled"].includes(r.status) && (
          <DropdownMenuItem
            data-testid={`${testidPrefix}-reject-${r.id}`}
            onClick={() => rejectReq(r)}
            className="cursor-pointer focus:bg-amber-500/10 focus:text-amber-300"
          >
            <XCircle size={16} className="mr-2 text-amber-400" /> Refuser
          </DropdownMenuItem>
        )}
        {r.status !== "archived" && (
          <DropdownMenuItem
            data-testid={`${testidPrefix}-archive-${r.id}`}
            onClick={() => archiveReq(r)}
            className="cursor-pointer focus:bg-zinc-700/30"
          >
            <Archive size={16} className="mr-2 text-zinc-400" /> Archiver
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem
          data-testid={`${testidPrefix}-delete-${r.id}`}
          onClick={() => del(r)}
          className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300"
        >
          <Trash size={16} className="mr-2" /> Supprimer
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div data-testid="requests-admin" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button data-testid="req-back-btn" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800 shrink-0">
              <ArrowLeft size={18} />
            </Button>
            <h1 className="text-base sm:text-lg font-medium text-white truncate">Demandes</h1>
          </div>
          <Button data-testid="req-admin-create-btn" onClick={() => setCreateOpen(true)} className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-9 shrink-0">
            <Plus size={16} weight="bold" className="mr-1" /> <span className="hidden sm:inline">Nouvelle demande</span><span className="sm:hidden">Nouveau</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-1 mb-4 border-b border-zinc-800 overflow-x-auto">
          {[
            { k: "open", l: "Ouvertes" },
            { k: "converted", l: "Converties" },
            { k: "rejected", l: "Refusées" },
            { k: "archived", l: "Archivées" },
            { k: "all", l: "Toutes" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`req-tab-${t.k}`}
              onClick={() => setFilter(t.k)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                filter === t.k ? "border-amber-500 text-amber-500" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div data-testid="req-empty" className="text-center py-16 text-zinc-500">Aucune demande</div>
        ) : (
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  {["Référence", "Agence", "Client", "Trajet", "Date", "Heure", "Pax/Bag", "Statut", "Tarif", "Actions"].map((h) => (
                    <th key={h} className="text-left py-2 px-3 text-xs text-zinc-500 uppercase tracking-wider font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`req-row-${r.id}`} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                    <td className="py-2 px-3 text-xs"><ReferenceBadge value={r.reference} size="xs" testid={`req-row-ref-${r.id}`} /></td>
                    <td className="py-2 px-3 text-sm text-white">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">
                          {r.agency_name || r.client_name || "—"}
                        </span>
                        <ClientTypeBadge request={r} />
                        {r.created_by_admin && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">admin</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-sm text-zinc-300">
                      <div>{r.client_name}</div>
                      <div className="text-xs text-zinc-500">{r.client_phone}</div>
                    </td>
                    <td className="py-2 px-3 text-sm text-zinc-300">
                      {r.is_round_trip ? (
                        <div className="space-y-0.5">
                          <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 font-medium">
                            <ArrowsLeftRight size={10} weight="bold" /> Aller-retour
                          </div>
                          <div><span className="text-zinc-500">Aller:</span> {r.pickup_location} → {r.dropoff_location}</div>
                          <div><span className="text-zinc-500">Retour:</span> {(r.return_pickup_location || r.dropoff_location)} → {(r.return_dropoff_location || r.pickup_location)}</div>
                        </div>
                      ) : (
                        <span>{r.pickup_location} → {r.dropoff_location}</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sm text-zinc-300">
                      {r.is_round_trip ? (
                        <div className="space-y-0.5">
                          <div>{fmtDate(r.pickup_date)}</div>
                          <div className="text-[11px] text-zinc-500">{fmtDate(r.return_date)}</div>
                        </div>
                      ) : fmtDate(r.pickup_date)}
                    </td>
                    <td className="py-2 px-3 text-sm text-zinc-300">
                      {r.is_round_trip ? (
                        <div className="space-y-0.5">
                          <div>{r.pickup_time}</div>
                          <div className="text-[11px] text-zinc-500">{r.return_time}</div>
                        </div>
                      ) : r.pickup_time}
                    </td>
                    <td className="py-2 px-3 text-sm text-zinc-400">{r.passenger_count}/{r.luggage_count} · {(r.vehicle_type || "eco").toUpperCase()}</td>
                    <td className="py-2 px-3"><Badge className={`${statusColor(r.status)} border text-xs whitespace-nowrap`}>{STATUS_LABEL[r.status] || r.status}</Badge></td>
                    <td className="py-2 px-3 text-sm text-amber-500 font-medium">
                      {r.admin_price != null ? (
                        <div className="flex flex-col">
                          <span>{r.admin_price} EUR</span>
                          {r.is_round_trip && r.pricing_mode && (
                            <span className="text-[10px] text-zinc-500 font-normal">
                              {r.pricing_mode === "round_trip_total" ? "total A/R (split)" : "par trajet (×2)"}
                            </span>
                          )}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setDetail(r)} className="h-7 px-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800" title="Voir"><Eye size={14} /></Button>
                        {["pending_price", "priced"].includes(r.status) && (
                          <Button data-testid={`req-price-${r.id}`} variant="ghost" size="sm" onClick={() => openPrice(r)} className="h-7 px-1.5 text-zinc-400 hover:text-amber-500 hover:bg-zinc-800" title="Définir le tarif">
                            <CurrencyEur size={14} />
                          </Button>
                        )}
                        <ActionsMenu r={r} testidPrefix="req-row" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`req-mcard-${r.id}`} className="bg-[#121212] border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <ReferenceBadge value={r.reference} size="sm" testid={`req-mcard-ref-${r.id}`} />
                <Badge className={`${statusColor(r.status)} border text-xs`}>{STATUS_LABEL[r.status] || r.status}</Badge>
              </div>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <p className="text-sm font-medium text-white truncate">
                      {r.agency_name || r.client_name || "—"}
                    </p>
                    <ClientTypeBadge request={r} />
                    {r.is_round_trip && <span data-testid={`req-mcard-rt-${r.id}`} className="text-[10px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 font-medium">A/R</span>}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{r.client_name} · {r.client_phone}</p>
                </div>
              </div>
              {r.is_round_trip ? (
                <div className="text-xs space-y-1 mb-2 p-2 rounded bg-amber-500/5 border border-amber-500/20">
                  <div><span className="text-zinc-500 uppercase tracking-wider text-[10px] mr-1">Aller</span><span className="text-zinc-200">{r.pickup_location} → {r.dropoff_location}</span></div>
                  <div className="text-[11px] text-zinc-400">{fmtDate(r.pickup_date)} · {r.pickup_time}</div>
                  <div><span className="text-zinc-500 uppercase tracking-wider text-[10px] mr-1">Retour</span><span className="text-zinc-200">{(r.return_pickup_location || r.dropoff_location)} → {(r.return_dropoff_location || r.pickup_location)}</span></div>
                  <div className="text-[11px] text-zinc-400">{fmtDate(r.return_date)} · {r.return_time}</div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-zinc-300 mb-2">{r.pickup_location} → {r.dropoff_location}</p>
                  <p className="text-xs text-zinc-400 mb-2">{fmtDate(r.pickup_date)} {r.pickup_time}</p>
                </>
              )}
              <p className="text-xs text-zinc-400 mb-2">{r.passenger_count}/{r.luggage_count} · {(r.vehicle_type || "eco").toUpperCase()}</p>
              {r.admin_price != null && (
                <p className="text-sm text-amber-500 font-semibold mb-2">
                  {r.admin_price} EUR
                  {r.is_round_trip && r.pricing_mode && (
                    <span className="ml-2 text-[10px] font-normal text-zinc-500">
                      ({r.pricing_mode === "round_trip_total" ? "total A/R" : "par trajet × 2"})
                    </span>
                  )}
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <Button data-testid={`req-mcard-view-${r.id}`} variant="ghost" size="sm" onClick={() => setDetail(r)} className="h-8 text-xs border border-zinc-800 text-zinc-300"><Eye size={13} className="mr-1" />Voir</Button>
                {["pending_price", "priced"].includes(r.status) && (
                  <Button data-testid={`req-mcard-price-${r.id}`} size="sm" onClick={() => openPrice(r)} className="h-8 text-xs bg-amber-500 text-black hover:bg-amber-600 font-semibold">
                    <CurrencyEur size={13} className="mr-1" /> Définir le tarif
                  </Button>
                )}
                <div className="ml-auto">
                  <ActionsMenu r={r} testidPrefix="req-mcard" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Price dialog */}
      <Dialog open={!!priceModal} onOpenChange={() => setPriceModal(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Définir le tarif</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {priceModal?.agency_name || priceModal?.client_name} — {priceModal?.pickup_location} → {priceModal?.dropoff_location}
            </DialogDescription>
          </DialogHeader>

          {priceModal?.is_round_trip && (
            <div data-testid="req-price-rt-info" className="p-3 rounded bg-amber-500/10 border border-amber-500/30 text-xs space-y-1.5">
              <div className="inline-flex items-center gap-1 text-amber-400 font-semibold uppercase tracking-wider text-[10px]">
                <ArrowsLeftRight size={12} weight="bold" /> Aller-retour
              </div>
              <div><span className="text-zinc-500">Aller :</span> <span className="text-zinc-100">{priceModal.pickup_location} → {priceModal.dropoff_location}</span> <span className="text-zinc-500">({fmtDate(priceModal.pickup_date)} {priceModal.pickup_time})</span></div>
              <div><span className="text-zinc-500">Retour :</span> <span className="text-zinc-100">{priceModal.return_pickup_location || priceModal.dropoff_location} → {priceModal.return_dropoff_location || priceModal.pickup_location}</span> <span className="text-zinc-500">({fmtDate(priceModal.return_date)} {priceModal.return_time})</span></div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Montant (EUR)</label>
              <Input
                data-testid="req-price-input"
                type="number"
                min={0}
                step="0.01"
                value={priceVal}
                onChange={(e) => setPriceVal(e.target.value)}
                placeholder="0.00"
                className="bg-[#1A1A1A] border-zinc-700 text-white h-11 text-center text-lg"
                autoFocus
              />
            </div>

            {priceModal?.is_round_trip && (
              <div data-testid="req-price-mode-group" className="space-y-2">
                <label className="text-xs text-zinc-400 uppercase tracking-wider block">
                  Mode de tarification <span className="text-red-400">*</span>
                </label>
                <label className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors ${pricingMode === "one_way_each" ? "border-amber-500 bg-amber-500/5" : "border-zinc-700 hover:border-zinc-600"}`}>
                  <input
                    type="radio"
                    name="pricing_mode"
                    value="one_way_each"
                    data-testid="req-price-mode-oneway"
                    checked={pricingMode === "one_way_each"}
                    onChange={() => setPricingMode("one_way_each")}
                    className="mt-0.5 accent-amber-500"
                  />
                  <div className="flex-1 text-xs">
                    <div className="text-white font-medium">Prix par trajet simple</div>
                    <div className="text-zinc-500">Chaque course recevra le montant saisi.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors ${pricingMode === "round_trip_total" ? "border-amber-500 bg-amber-500/5" : "border-zinc-700 hover:border-zinc-600"}`}>
                  <input
                    type="radio"
                    name="pricing_mode"
                    value="round_trip_total"
                    data-testid="req-price-mode-total"
                    checked={pricingMode === "round_trip_total"}
                    onChange={() => setPricingMode("round_trip_total")}
                    className="mt-0.5 accent-amber-500"
                  />
                  <div className="flex-1 text-xs">
                    <div className="text-white font-medium">Prix total aller-retour</div>
                    <div className="text-zinc-500">Le montant sera réparti équitablement entre l'aller et le retour.</div>
                  </div>
                </label>
              </div>
            )}

            {/* Live preview */}
            {priceVal && parseFloat(priceVal) > 0 && (
              <div data-testid="req-price-preview" className="rounded-lg border border-zinc-800 bg-[#0b0b0b] p-3 text-xs space-y-1">
                <div className="text-zinc-400 uppercase tracking-wider text-[10px] mb-1">Aperçu — {priceModal?.is_round_trip ? "2 courses seront créées" : "1 course sera créée"}</div>
                {priceModal?.is_round_trip ? (
                  (() => {
                    const p = parseFloat(priceVal);
                    const out = pricingMode === "round_trip_total" ? +(p / 2).toFixed(2) : p;
                    const ret = pricingMode === "round_trip_total" ? +(p - out).toFixed(2) : p;
                    const total = pricingMode === "round_trip_total" ? p : +(p * 2).toFixed(2);
                    return (
                      <>
                        <div className="flex justify-between"><span className="text-zinc-500">Aller</span><span className="text-zinc-100">{out.toFixed(2)} EUR</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Retour</span><span className="text-zinc-100">{ret.toFixed(2)} EUR</span></div>
                        <div className="flex justify-between border-t border-zinc-800 pt-1 mt-1"><span className="text-amber-500 font-semibold">Total chauffeur</span><span className="text-amber-500 font-semibold">{total.toFixed(2)} EUR</span></div>
                      </>
                    );
                  })()
                ) : (
                  <div className="flex justify-between"><span className="text-zinc-500">1 course</span><span className="text-amber-500 font-semibold">{parseFloat(priceVal).toFixed(2)} EUR</span></div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button data-testid="req-price-cancel" variant="outline" onClick={() => setPriceModal(null)} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Annuler</Button>
              <Button data-testid="req-price-submit" onClick={submitPrice} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600">Envoyer le tarif</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {detail?.agency_name || detail?.client_name || "Demande"}
              {detail?.is_round_trip && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 font-medium inline-flex items-center gap-1">
                  <ArrowsLeftRight size={10} weight="bold" /> Aller-retour
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">{detail?.pickup_location} → {detail?.dropoff_location}</DialogDescription>
            {detail?.reference && (
              <div className="pt-1.5">
                <ReferenceBadge value={detail.reference} size="sm" testid={`req-detail-ref-${detail.id}`} />
              </div>
            )}
          </DialogHeader>
          {detail && (
            <div className="space-y-1.5 text-sm">
              {[
                ["Référence", detail.reference || "—"],
                ["Client", `${detail.client_name} · ${detail.client_phone}`],
                ["Email", detail.client_email || "—"],
                ["Type", detail.is_round_trip ? "Aller-retour" : "Aller simple"],
                ["Aller", `${detail.pickup_location} → ${detail.dropoff_location}`],
                ["Aller — quand", `${fmtDate(detail.pickup_date)} ${detail.pickup_time}`],
                ...(detail.is_round_trip ? [
                  ["Retour", `${detail.return_pickup_location || detail.dropoff_location} → ${detail.return_dropoff_location || detail.pickup_location}`],
                  ["Retour — quand", `${fmtDate(detail.return_date)} ${detail.return_time}`],
                ] : []),
                ["Passagers", `${detail.passenger_count}`],
                ["Bagages", `${detail.luggage_count}`],
                ["Véhicule", (detail.vehicle_type || "eco").toUpperCase()],
                ["Équipement enfant", detail.child_equipment || "—"],
                ["Notes", detail.notes || "—"],
                ["Statut", STATUS_LABEL[detail.status] || detail.status],
                ["Tarif", detail.admin_price != null ? `${detail.admin_price} EUR` : "—"],
                ...(detail.is_round_trip && detail.pricing_mode ? [
                  ["Mode", detail.pricing_mode === "round_trip_total" ? "Prix total aller-retour (réparti)" : "Prix par trajet simple (×2)"]
                ] : []),
                ["Créée le", fmtDateTime(detail.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-zinc-800/50 py-1.5">
                  <span className="text-xs text-zinc-500 uppercase tracking-wider">{k}</span>
                  <span className="text-zinc-100 text-right">{v}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdminCreateRequestModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchRequests(); }} />
      <ClientAccountShareModal
        open={!!shareModal}
        data={shareModal?.data}
        ride={shareModal?.ride}
        onClose={() => setShareModal(null)}
      />
    </div>
  );
}
